import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
	DEFAULT_BLOCK_LINK_HASH_PREFIX,
	extractBlockIdFromLinkHash,
} from '@atlaskit/editor-common/block-menu';

import { getHeadingIdsToElements } from '@confluence/editor-heading-links/entry-points/headingLinksFromElements.ts';
import { expValEquals } from '@confluence/feature-experiments';

// find editor node dom with localId
const getLocalIdSelector = (localId: string) => {
	// Check if the element with data-local-id exists
	if (document.querySelector(`[data-local-id="${localId}"]`)) {
		return document.querySelector(`[data-local-id="${localId}"]`);
	}
	// special case for tables, decision lists, and task lists which already have localId
	if (document.querySelector(`[data-table-local-id="${localId}"]`)) {
		return document.querySelector(`[data-table-local-id="${localId}"]`);
	}
	if (document.querySelector(`[data-decision-list-local-id="${localId}"]`)) {
		return document.querySelector(`[data-decision-list-local-id="${localId}"]`);
	}
	if (document.querySelector(`[data-task-list-local-id="${localId}"]`)) {
		return document.querySelector(`[data-task-list-local-id="${localId}"]`);
	}
	// special case for extension, smart cards and media
	if (document.querySelector(`[localid="${localId}"]`)) {
		return document.querySelector(`[localid="${localId}"]`);
	}
	return null;
};

export const useScrollOnUrlChange = (
	hasProseMirrorRendered: boolean,
	blockLinkHashPrefix = DEFAULT_BLOCK_LINK_HASH_PREFIX,
) => {
	const { hash } = useLocation();

	useEffect(() => {
		if (!hasProseMirrorRendered || !hash) {
			return;
		}

		if (expValEquals('platform_editor_block_menu', 'isEnabled', true)) {
			const blockId = extractBlockIdFromLinkHash(hash, blockLinkHashPrefix);
			if (blockId) {
				const localIdSelector = getLocalIdSelector(blockId);
				localIdSelector?.scrollIntoView();
				return;
			}
		}

		const id = hash.slice(1);

		const element = document.getElementById(id);
		// Indicates anchor
		if (element) {
			element.scrollIntoView();
		}

		const headingIdToElement = getHeadingIdsToElements();
		const scrollContainer = document.getElementsByClassName(
			'fabric-editor-popup-scroll-parent',
		)?.[0];

		// The top of the heading element
		const headingTop = headingIdToElement.get(id)?.getBoundingClientRect()?.top;
		// The top of the scroll container
		const scrollContainerTop = scrollContainer?.getBoundingClientRect()?.top;
		// The current scroll position
		const offset = scrollContainer?.scrollTop;

		if (headingTop === undefined || scrollContainerTop === undefined || offset === undefined) {
			return;
		}

		// Subtract the scroll container position from the heading position to get the scroll distance
		// and add the offset to account for current scroll position
		const scrollToY = headingTop - scrollContainerTop + offset;

		scrollContainer?.scrollTo({
			left: 0,
			top: scrollToY,
			behavior: 'smooth',
		});
	}, [hash, blockLinkHashPrefix, hasProseMirrorRendered]);
};
